import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15;

/**
 * Post-Stripe-Checkout landing. Polls /api/payments/status/:session_id
 * until it's paid (or times out). The status endpoint also fills the
 * webhook gap by hitting Stripe directly when needed.
 */
export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, err: "" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, err: "Missing session_id in URL." });
      return;
    }
    let stopped = false;
    let cur = 0;

    const tick = async () => {
      if (stopped) return;
      cur += 1;
      setAttempts(cur);
      try {
        const r = await axios.get(`${API}/api/payments/status/${sessionId}`);
        const d = r.data || {};
        if (d.payment_status === "paid") {
          setState({ status: "paid", data: d, err: "" });
          return;
        }
        if (["failed", "expired", "refunded"].includes(d.payment_status)) {
          setState({ status: d.payment_status, data: d, err: "" });
          return;
        }
        if (cur >= MAX_POLL_ATTEMPTS) {
          setState({ status: "timeout", data: d, err: "" });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: "error", data: null,
          err: e?.response?.data?.detail || e?.message || "Unable to load status." });
      }
    };
    tick();
    return () => { stopped = true; };
  }, [sessionId]);

  const kindLabel = {
    topup: "Top-up (+10 uses)",
    access_bonus: "Access Bonus Unlimited (+50 uses)",
    early_access: "Early Access Package (+175 shared uses)",
  }[state.data?.kind] || "Purchase";

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 font-mono flex items-center justify-center p-6">
      <div className="max-w-lg w-full border border-[#27272A] bg-[#0B0B0B] p-8" data-testid="billing-success-panel">
        {state.status === "polling" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-emerald-400" size={40} />
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">CONFIRMING_PAYMENT</div>
            <div className="text-[13px] text-zinc-400 text-center">
              Waiting for Stripe to confirm your purchase…{" "}
              <span className="text-zinc-500">(attempt {attempts}/{MAX_POLL_ATTEMPTS})</span>
            </div>
          </div>
        )}
        {state.status === "paid" && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle2 className="text-emerald-400" size={44} />
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">PAYMENT_CONFIRMED</div>
            <h1 className="text-2xl">{kindLabel} — success</h1>
            <p className="text-[13px] text-zinc-300 text-center">
              Your credits are already on your account. Head to the dashboard to use them.
            </p>
            <div className="flex gap-2 mt-2">
              <Link to="/pricing" data-testid="billing-success-back-pricing"
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-600 hover:bg-zinc-900">
                BACK_TO_PRICING
              </Link>
              <Link to="/dashboard" data-testid="billing-success-goto-dashboard"
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.2em] border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
                OPEN_DASHBOARD
              </Link>
            </div>
          </div>
        )}
        {["failed", "expired"].includes(state.status) && (
          <div className="flex flex-col items-center gap-4">
            <AlertTriangle className="text-rose-400" size={40} />
            <div className="text-[11px] uppercase tracking-[0.2em] text-rose-300">
              PAYMENT_{state.status.toUpperCase()}
            </div>
            <p className="text-[13px] text-zinc-300 text-center">
              Your purchase did not complete. No charge was made. You can try again from the pricing page.
            </p>
            <Link to="/pricing" className="px-4 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-600 hover:bg-zinc-900">
              BACK_TO_PRICING
            </Link>
          </div>
        )}
        {state.status === "timeout" && (
          <div className="flex flex-col items-center gap-4">
            <AlertTriangle className="text-amber-400" size={40} />
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">STILL_PROCESSING</div>
            <p className="text-[13px] text-zinc-300 text-center">
              Stripe is taking longer than usual. Your credits will appear as soon as the payment settles —
              refresh this page in a minute or check your account.
            </p>
            <Link to="/pricing" className="px-4 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-600 hover:bg-zinc-900">
              BACK_TO_PRICING
            </Link>
          </div>
        )}
        {state.status === "error" && (
          <div className="flex flex-col items-center gap-4">
            <AlertTriangle className="text-rose-400" size={40} />
            <div className="text-[11px] uppercase tracking-[0.2em] text-rose-300">ERROR</div>
            <p className="text-[13px] text-zinc-300 text-center">{state.err}</p>
            <Link to="/pricing" className="px-4 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-600 hover:bg-zinc-900">
              BACK_TO_PRICING
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
